import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';
import { AuthService } from './auth.service';
import { HttpClient } from '@angular/common/http';
import { SpinnerService } from './spinner.service';
import { ToastService } from './toast.service';
import { IServerResponse, ServerResponse } from '../models/server-response.model';
import { IReport, Report } from '../models/report.model';
import { IReportsResponse, ReportsResponse } from '../models/reports-response.model';
import { IReportInsights, ReportInsights } from '../models/report-insights.model';
import { IPresignResponse } from '../models/presign-response.model';
import { AwsService } from './aws.service';
import { IUploadPosition } from '../models/upload-progress.model';

@Injectable({
  providedIn: 'root'
})
export class ReportService {

  public loading: boolean = false;
  public reports: ReportsResponse = new ReportsResponse({
    pendings: [],
    inProgress: [],
    resolved: [],
    canceled: []
  });

  /** Flag propio de la lectura de indicadores: no reinicia el listado de
      reports-page ni bloquea la pantalla con el spinner global. */
  public insightsLoading: boolean = false;
  public insights?: ReportInsights;

  constructor(
    public authService: AuthService,
    private awsService: AwsService,
    private http: HttpClient,
    public spinnerService: SpinnerService,
    public toastService: ToastService
  ) { }

  /* -------------------------------------------------------------------------- */
  /*                                ANCHOR cancel                               */
  /* -------------------------------------------------------------------------- */
  // region cancel
  /**
   * Cancela un reporte. No borra nada: el backend pone `active` en 0, guarda
   * quién canceló y la razón, y el reporte conserva su status.
   *
   * La razón llega ya recortada desde el modal: el backend rechaza con 400 una
   * cadena vacía o de solo espacios.
   */
  async cancel(
    report: Report,
    cancelReason: string
  ): Promise<boolean> {

    let flag = false;

    const urlApi = `${environment.urlApi}/neighborhoods/${report.neighborhoodId}/reports/${report.reportId}/cancel`;

    this.spinnerService.loading = true;
    this.spinnerService.message = 'Cancelando reporte';

    try {

      const token = await this.authService.getToken();

      const response = await new Promise<ServerResponse>(
        (resolve, reject) => {
          this.http.put<IServerResponse>(
            urlApi,
            JSON.stringify({
              cancelReason
            }),
            {
              headers: {
                "content-type": "application/json",
                "Authorization": token.toString()
              }
            }
          ).subscribe({
            next: (
                response: IServerResponse
              ) => resolve(
                new ServerResponse(response)
              ),
            error: (error) => {
              if (error.error) {
                resolve(new ServerResponse(error.error));
              } else {
                reject(error);
                this.toastService.show(
                  {
                    header: 'Error de servidor',
                    body: `${error}`,
                    classname: 'bg-danger text-white'
                  }
                );
              }
            },
          });
        }
      );

      console.log(response);

      if(response.statusCode === 200) {
        flag = true;

        this.toastService.show(
          {
            body: `${response.message}`,
            classname: 'bg-success text-white'
          }
        );

        this.getAll(
          report.neighborhoodId
        );

      } else {

        this.toastService.show(
          {
            header: 'Error de servidor',
            body: `${response.message}`,
            classname: 'bg-danger text-white'
          }
        );

      }

    } catch(error) {

      console.log(error);

      this.toastService.show(
        {
          header: 'Error',
          body: `${error}`,
          classname: 'bg-danger text-white'
        }
      );

    } finally {

      this.spinnerService.loading = false;
      this.spinnerService.message = '';

    }

    return flag;

  }

  /* -------------------------------------------------------------------------- */
  /*                                ANCHOR create                               */
  /* -------------------------------------------------------------------------- */
  // region create
  async create(
    neighborhoodId: string,
    report: any
  ): Promise<Report | undefined> {

    let reportResult: Report | undefined;

    const urlApi = `${environment.urlApi}/neighborhoods/${neighborhoodId}/reports`;

    this.spinnerService.loading = true;
    this.spinnerService.message = 'Registrando reporte';

    try {

      const token = await this.authService.getToken();

      const response = await new Promise<ServerResponse<{ report: IReport }>>(
        (resolve, reject) => {
          this.http.post<IServerResponse<{ report: IReport }>>(
            urlApi,
            JSON.stringify(report),
            {
              headers: {
                "content-type": "application/json",
                "Authorization": token.toString()
              }
            }
          ).subscribe({
            next: (
                response: IServerResponse<{ report: IReport }>
              ) => resolve(
                new ServerResponse<{ report: IReport }>(response)
              ),
            error: (error) => {
              if (error.error) {
                resolve(new ServerResponse(error.error));
              } else {
                reject(error);
                this.toastService.show(
                  {
                    header: 'Error de servidor',
                    body: `${error}`,
                    classname: 'bg-danger text-white'
                  }
                );
              }
            },
          });
        }
      );

      console.log(response);

      if(response.statusCode === 201) {
        
        reportResult = new Report(response.data!.report);

        this.toastService.show(
          {
            body: `${response.message}`,
            classname: 'bg-success text-white'
          }
        );

        this.getAll(
          neighborhoodId
        );

      } else {

        this.toastService.show(
          {
            header: 'Error de servidor',
            body: `${response.message}`,
            classname: 'bg-danger text-white'
          }
        );

      }

    } catch(error) {

      console.log(error);

      this.toastService.show(
        {
          header: 'Error',
          body: `${error}`,
          classname: 'bg-danger text-white'
        }
      );

    } finally {

      this.spinnerService.loading = false;
      this.spinnerService.message = '';

    }

    return reportResult;

  }

  /* -------------------------------------------------------------------------- */
  /*                                ANCHOR delete                               */
  /* -------------------------------------------------------------------------- */
  // region delete
  async delete(
    report: Report
  ): Promise<boolean> {

    let flag = false;

    const urlApi = `${environment.urlApi}/neighborhoods/${report.neighborhoodId}/reports/${report.reportId}`;

    this.spinnerService.loading = true;
    this.spinnerService.message = 'Eliminando reporte';

    try {

      const token = await this.authService.getToken();

      const response = await new Promise<ServerResponse>(
        (resolve, reject) => {
          this.http.delete<IServerResponse>(
            urlApi,
            {
              headers: {
                "content-type": "application/json",
                "Authorization": token.toString()
              }
            }
          ).subscribe({
            next: (
              response: IServerResponse
            ) => resolve(
              new ServerResponse(response)
            ),
            error: (error) => {
              if (error.error) {
                resolve(new ServerResponse(error.error));
              } else {
                reject(error);
                this.toastService.show(
                  {
                    header: 'Error de servidor',
                    body: `${error}`,
                    classname: 'bg-danger text-white'
                  }
                );
              }
            },
          });
        }
      );

      console.log(response);

      if(response.statusCode === 200) {
        flag = true;

        this.toastService.show(
          {
            body: `${response.message}`,
            classname: 'bg-success text-white'
          }
        );

        this.getAll(
          report.neighborhoodId
        );

      } else {

        this.toastService.show(
          {
            header: 'Error de servidor',
            body: `${response.message}`,
            classname: 'bg-danger text-white'
          }
        );

      }

    } catch(error) {

      console.log(error);

      this.toastService.show(
        {
          header: 'Error',
          body: `${error}`,
          classname: 'bg-danger text-white'
        }
      );

    } finally {

      this.spinnerService.loading = false;
      this.spinnerService.message = '';

    }

    return flag;

  }

  /* -------------------------------------------------------------------------- */
  /*                                 ANCHOR get                                 */
  /* -------------------------------------------------------------------------- */
  // region get
  async get(
    report: Report
  ): Promise<Report | undefined> {

    let reportResult: Report | undefined;

    this.spinnerService.loading = true;
    this.spinnerService.message = 'Cargando reporte';

    const urlApi = `${environment.urlApi}/neighborhoods/${report.neighborhoodId}/reports/${report.reportId}`;

    try {

      const token = await this.authService.getToken();

      const response = await new Promise<ServerResponse<{ report: IReport }>>(
        (resolve, reject) => {
          this.http.get<IServerResponse<{ report: IReport }>>(
            urlApi,
            {
              headers: {
                "content-type": "application/json",
                "Authorization": token.toString()
              }
            }
          ).subscribe({
            next: (
                response: IServerResponse<{ report: IReport }>
              ) => resolve(
                new ServerResponse<{ report: IReport }>(response)
              ),
            error: (error) => {
              if (error.error) {
                resolve(new ServerResponse(error.error));
              } else {

                this.toastService.show(
                  {
                    header: 'Error de servidor',
                    body: `${error}`,
                    classname: 'bg-danger text-white'
                  }
                );

                reject(error);
              }
            },
          });
        }
      );

      console.log(response);

      if(response.statusCode === 200) {

        reportResult = new Report(response.data!.report);

      } else {

        this.toastService.show(
          {
            header: 'Error de servidor',
            body: `${response.message}`,
            classname: 'bg-danger text-white'
          }
        );

      }

    } catch(error) {

      console.log(error);

      this.toastService.show(
        {
          header: 'Error',
          body: `${error}`,
          classname: 'bg-danger text-white'
        }
      );

    } finally {

      this.spinnerService.loading = false;
      this.spinnerService.message = '';

    }

    return reportResult;

  }

  /* -------------------------------------------------------------------------- */
  /*                                ANCHOR getAll                               */
  /* -------------------------------------------------------------------------- */
  // region getAll
  async getAll(
    neighborhoodId: string
  ) {

    this.loading = true;

    const urlApi = `${environment.urlApi}/neighborhoods/${neighborhoodId}/reports`;

    try {

      const token = await this.authService.getToken();

      const response = await new Promise<ServerResponse<IReportsResponse>>(
        (resolve, reject) => {
          this.http.get<IServerResponse<IReportsResponse>>(
            urlApi,
            {
              headers: {
                "content-type": "application/json",
                "Authorization": token.toString()
              }
            }
          ).subscribe({
            next: (
                response: IServerResponse<IReportsResponse>
              ) => resolve(
                new ServerResponse<IReportsResponse>(response)
              ),
            error: (error) => {
              if (error.error) {
                resolve(new ServerResponse(error.error));
              } else {

                this.toastService.show(
                  {
                    header: 'Error de servidor',
                    body: `${error}`,
                    classname: 'bg-danger text-white'
                  }
                );

                reject(error);
              }
            },
          });
        }
      );

      console.log(response);

      if(response.statusCode === 200) {

        this.reports = new ReportsResponse(response.data!);

      } else {

        this.toastService.show(
          {
            header: 'Error de servidor',
            body: `${response.message}`,
            classname: 'bg-danger text-white'
          }
        );

      }

    } catch(error) {

      console.log(error);

      this.toastService.show(
        {
          header: 'Error',
          body: `${error}`,
          classname: 'bg-danger text-white'
        }
      );

    } finally {

      this.loading = false;

    }

  }

  /* -------------------------------------------------------------------------- */
  /*                              ANCHOR getInsights                             */
  /* -------------------------------------------------------------------------- */
  // region getInsights
  async getInsights(
    neighborhoodId: string
  ): Promise<void> {

    this.insightsLoading = true;

    const urlApi = `${environment.urlApi}/neighborhoods/${neighborhoodId}/reports/insights`;

    try {

      const token = await this.authService.getToken();

      const response = await new Promise<ServerResponse<{ insights: IReportInsights }>>(
        (resolve, reject) => {
          this.http.get<IServerResponse<{ insights: IReportInsights }>>(
            urlApi,
            {
              headers: {
                "content-type": "application/json",
                "Authorization": token.toString()
              }
            }
          ).subscribe({
            next: (
                response: IServerResponse<{ insights: IReportInsights }>
              ) => resolve(
                new ServerResponse<{ insights: IReportInsights }>(response)
              ),
            error: (error) => {
              if (error.error) {
                resolve(new ServerResponse(error.error));
              } else {

                this.toastService.show(
                  {
                    header: 'Error de servidor',
                    body: `${error}`,
                    classname: 'bg-danger text-white'
                  }
                );

                reject(error);
              }
            },
          });
        }
      );

      if(response.statusCode === 200) {

        this.insights = new ReportInsights(response.data!.insights);

      } else {

        this.insights = undefined;

        this.toastService.show(
          {
            header: 'Error de servidor',
            body: `${response.message}`,
            classname: 'bg-danger text-white'
          }
        );

      }

    } catch(error) {

      console.log(error);

      this.toastService.show(
        {
          header: 'Error',
          body: `${error}`,
          classname: 'bg-danger text-white'
        }
      );

    } finally {

      this.insightsLoading = false;

    }

  }

  /* -------------------------------------------------------------------------- */
  /*                                ANCHOR update                               */
  /* -------------------------------------------------------------------------- */
  // region update
  async update(
    report: Report
  ): Promise<boolean> {

    let flag = false;

    const urlApi = `${environment.urlApi}/neighborhoods/${report.neighborhoodId}/reports/${report.reportId}`;

    this.spinnerService.loading = true;
    this.spinnerService.message = 'Actualizando reporte';

    try {

      const token = await this.authService.getToken();

      const response = await new Promise<ServerResponse>(
        (resolve, reject) => {
          this.http.put<IServerResponse>(
            urlApi,
            JSON.stringify({
              status: report.nextStatus
            }),
            {
              headers: {
                "content-type": "application/json",
                "Authorization": token.toString()
              }
            }
          ).subscribe({
            next: (
                response: IServerResponse
              ) => resolve(
                new ServerResponse(response)
              ),
            error: (error) => {
              if (error.error) {
                resolve(new ServerResponse(error.error));
              } else {
                reject(error);
                this.toastService.show(
                  {
                    header: 'Error de servidor',
                    body: `${error}`,
                    classname: 'bg-danger text-white'
                  }
                );
              }
            },
          });
        }
      );

      console.log(response);

      if(response.statusCode === 200) {
        flag = true;

        this.toastService.show(
          {
            body: `${response.message}`,
            classname: 'bg-success text-white'
          }
        );

        this.getAll(
          report.neighborhoodId
        );

      } else {

        this.toastService.show(
          {
            header: 'Error de servidor',
            body: `${response.message}`,
            classname: 'bg-danger text-white'
          }
        );

      }

    } catch(error) {

      console.log(error);

      this.toastService.show(
        {
          header: 'Error',
          body: `${error}`,
          classname: 'bg-danger text-white'
        }
      );

    } finally {

      this.spinnerService.loading = false;
      this.spinnerService.message = '';

    }

    return flag;

  }

  /* -------------------------------------------------------------------------- */
  /*                             ANCHOR uploadImage                             */
  /* -------------------------------------------------------------------------- */
  // region uploadImage
  async uploadImage(
    report: Report,
    file: File,
    contentType: string,
    fileName?: string,
    position: IUploadPosition | null = null
  ): Promise<boolean> {

    let flag = false;

    const urlApi = `${environment.urlApi}/neighborhoods/${report.neighborhoodId}/reports/${report.reportId}/files`;

    this.spinnerService.loading = true;
    this.spinnerService.startUpload(fileName ?? file.name, position);

    try {

      const token = await this.authService.getToken();

      const response = await new Promise<ServerResponse<{presignFile: IPresignResponse}>>(
        (resolve, reject) => {
          this.http.put<IServerResponse<{presignFile: IPresignResponse}>>(
            urlApi,
            JSON.stringify({
              contentType
            }),
            {
              headers: {
                'content-type': 'application/json',
                'Authorization': token.toString()
              }
            }
          ).subscribe({
            next: (
              response: IServerResponse<{presignFile: IPresignResponse}>
            ) => resolve(
              new ServerResponse<{presignFile: IPresignResponse}>(response)
            ),
            error: (error) => {
              if (error.error) {
                resolve(new ServerResponse(error.error));
              } else {
                this.toastService.show({
                  header: 'Error de servidor',
                  body: `${error}`,
                  classname: 'bg-danger text-white'
                });
                reject(error);
              }
            }
          });
        }
      );
  
      if (response.statusCode === 201) {
        
        flag = await this.awsService.uploadFile(
          response.data!.presignFile.uploadUrl,
          file,
          contentType,
          fileName ?? file.name,
          position
        );

        if (flag) {

          this.toastService.show({
            body: `${response.message}`,
            classname: 'bg-success text-white'
          });

        } else {

          this.toastService.show({
            header: 'Error',
            body: `No se pudo subir la imagen.`,
            classname: 'bg-danger text-white'
          });

        }
        
      } else {

        this.toastService.show({
          header: 'Error de servidor',
          body: `${response.message}`,
          classname: 'bg-danger text-white'
        });

      }

    } catch (error) {
      console.log(error);

      this.toastService.show({
        header: 'Error',
        body: `${error}`,
        classname: 'bg-danger text-white'
      });

    }

    this.spinnerService.loading = false;
    this.spinnerService.message = '';
    this.spinnerService.clearUpload();

    return flag;

  }

}
